import { useEffect, useState } from "react";
import { Plus, X, Pencil, Trash2, MapPin, Calendar, Clock, Users } from "lucide-react";
import { toast } from "sonner";
import { api, inr, formatApiError } from "../../lib/api";

const inputCls = "w-full bg-[#0F0F0F] border border-white/10 text-white text-sm px-3 py-2.5 rounded-sm placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]";
const labelCls = "block text-[10px] uppercase tracking-widest text-white/40 mb-1.5";

const AGE_GROUPS = [...Array.from({ length: 13 }, (_, i) => `U${i + 6}`), "Open"];

const emptyForm = { name: "", start_date: "", end_date: "", location: "", registration_fee: "", timings: "", age_groups: [], notes: "" };

const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "long", year: "numeric" }) : "";

const dateRange = (t) => {
  const s = t.start_date || t.date;
  const e = t.end_date;
  if (!s) return "";
  if (!e || e === s) return formatDate(s);
  return `${formatDate(s)} — ${formatDate(e)}`;
};

export default function Tournaments() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = () => api.get("/admin/tournaments").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openAdd = () => { setEditing(null); setForm(emptyForm); setShowForm(true); };
  const openEdit = (t) => {
    setEditing(t.id);
    setForm({
      ...emptyForm, ...t,
      start_date: t.start_date || t.date || "",
      end_date: t.end_date || "",
      registration_fee: t.registration_fee ?? "",
      age_groups: Array.isArray(t.age_groups) ? t.age_groups : (t.team_name ? [t.team_name] : []),
      notes: t.notes || "",
    });
    setShowForm(true);
  };

  const toggleAge = (ag) => {
    const next = form.age_groups.includes(ag)
      ? form.age_groups.filter((x) => x !== ag)
      : [...form.age_groups, ag];
    setForm({ ...form, age_groups: next });
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const body = {
        name: form.name,
        start_date: form.start_date,
        end_date: form.end_date || form.start_date,
        location: form.location,
        registration_fee: Number(form.registration_fee) || 0,
        timings: form.timings,
        age_groups: form.age_groups,
        notes: form.notes,
      };
      if (editing) {
        await api.put(`/admin/tournaments/${editing}`, body);
        toast.success("Tournament updated");
      } else {
        await api.post("/admin/tournaments", body);
        toast.success("Tournament added");
      }
      setShowForm(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this tournament?")) return;
    try {
      await api.delete(`/admin/tournaments/${id}`);
      toast.success("Tournament removed");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = items.filter((t) => (t.end_date || t.start_date || t.date) >= today);
  const past = items.filter((t) => (t.end_date || t.start_date || t.date) < today);

  const Card = ({ t }) => (
    <div className="border border-white/10 bg-[#0A0A0A] p-5" data-testid={`tournament-${t.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <h3 className="font-heading font-black text-lg uppercase tracking-tight truncate">{t.name}</h3>
          {Array.isArray(t.age_groups) && t.age_groups.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2" data-testid={`tournament-ages-${t.id}`}>
              {t.age_groups.map((a) => (
                <span key={a} className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-[#D4AF37]/50 text-[#D4AF37]">{a}</span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => openEdit(t)} className="text-white/50 hover:text-[#D4AF37] p-1.5" data-testid={`tournament-edit-${t.id}`}>
            <Pencil size={15} />
          </button>
          <button onClick={() => remove(t.id)} className="text-red-400/70 hover:text-red-400 p-1.5" data-testid={`tournament-delete-${t.id}`}>
            <Trash2 size={15} />
          </button>
        </div>
      </div>
      <div className="space-y-1.5 text-xs text-white/60">
        <p className="flex items-center gap-2"><Calendar size={12} className="text-[#D4AF37]" /> {dateRange(t)}</p>
        {t.timings && <p className="flex items-center gap-2"><Clock size={12} className="text-[#D4AF37]" /> {t.timings}</p>}
        {t.location && <p className="flex items-center gap-2"><MapPin size={12} className="text-[#D4AF37]" /> {t.location}</p>}
        {t.registration_fee > 0 && <p className="text-[#D4AF37] font-semibold pt-1">Reg Fee: {inr(t.registration_fee)}</p>}
        {t.notes && <p className="text-white/50 pt-1 border-t border-white/5 mt-2">{t.notes}</p>}
      </div>
    </div>
  );

  return (
    <div data-testid="admin-tournaments">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="font-heading text-3xl font-black uppercase tracking-tight">Tournaments <span className="text-[#D4AF37]">({items.length})</span></h1>
        <button onClick={openAdd} className="flex items-center gap-2 bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-sm hover:bg-[#F3D059] transition-colors duration-200" data-testid="tournament-add">
          <Plus size={15} /> Add Tournament
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center px-4 overflow-y-auto py-10" onClick={() => setShowForm(false)} data-testid="tournament-modal">
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-[#0A0A0A] border border-white/10 p-6 sm:p-8 w-full max-w-2xl">
            <div className="flex justify-between items-start mb-6">
              <h2 className="font-heading text-xl font-black">{editing ? "Edit Tournament" : "New Tournament"}</h2>
              <button type="button" onClick={() => setShowForm(false)} className="text-white/50 hover:text-white"><X size={20} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><label className={labelCls}>Tournament Name *</label><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="tf-name" /></div>
              <div><label className={labelCls}>Start Date *</label><input required type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className={inputCls} data-testid="tf-start" /></div>
              <div><label className={labelCls}>End Date</label><input type="date" value={form.end_date} min={form.start_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className={inputCls} data-testid="tf-end" /></div>
              <div><label className={labelCls}>Timings</label><input placeholder="e.g. 9 AM – 5 PM" value={form.timings} onChange={(e) => setForm({ ...form, timings: e.target.value })} className={inputCls} data-testid="tf-timings" /></div>
              <div><label className={labelCls}>Registration Fee (₹)</label><input type="number" min="0" value={form.registration_fee} onChange={(e) => setForm({ ...form, registration_fee: e.target.value })} className={inputCls} data-testid="tf-fee" /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Location</label><input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inputCls} data-testid="tf-location" /></div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Age Groups *</label>
                <div className="flex flex-wrap gap-2" data-testid="tf-age-groups">
                  {AGE_GROUPS.map((ag) => {
                    const on = form.age_groups.includes(ag);
                    return (
                      <button key={ag} type="button" onClick={() => toggleAge(ag)}
                        className={`text-xs uppercase tracking-widest px-3 py-1.5 border transition-colors duration-150 ${on ? "bg-[#D4AF37] text-black border-[#D4AF37]" : "border-white/15 text-white/60 hover:border-[#D4AF37]"}`}
                        data-testid={`tf-age-${ag}`}>
                        {ag}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="sm:col-span-2"><label className={labelCls}>Notes (optional)</label><textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} data-testid="tf-notes" /></div>
            </div>
            <div className="flex gap-3 pt-6">
              <button type="submit" className="bg-[#D4AF37] text-black font-bold uppercase tracking-widest text-xs px-8 py-3 rounded-sm hover:bg-[#F3D059] transition-colors duration-200" data-testid="tf-save">
                {editing ? "Save Changes" : "Add Tournament"}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="border border-white/20 text-white/70 text-xs uppercase tracking-widest px-6 py-3 rounded-sm hover:border-[#D4AF37]" data-testid="tf-cancel">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {items.length === 0 && (
        <div className="border border-white/10 bg-[#0A0A0A] p-10 text-center" data-testid="tournaments-empty">
          <p className="text-white/40 text-sm">No tournaments yet. Add one to notify players and parents.</p>
        </div>
      )}

      {upcoming.length > 0 && (
        <>
          <h2 className="text-xs tracking-[0.25em] uppercase text-[#D4AF37] font-semibold mb-4">Upcoming</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
            {upcoming.map((t) => <Card key={t.id} t={t} />)}
          </div>
        </>
      )}

      {past.length > 0 && (
        <>
          <h2 className="text-xs tracking-[0.25em] uppercase text-white/40 font-semibold mb-4">Past</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 opacity-70">
            {past.map((t) => <Card key={t.id} t={t} />)}
          </div>
        </>
      )}
    </div>
  );
}
